import { useEffect, useState } from "react";
import type { ApiResponseNormalizer, TableColumn, TableVariant } from "@/types/table";
import { normalizeApiResponse } from "@/lib/tableNormalizers";
import { fetchUtilisationReport } from "@/analytics/utilisationReports";
import type { UtilisationFilters } from "@/analytics/types";
import { applySort, DEFAULT_SORT, type SortDir } from "@/lib/tableSort";
import { UtilisationDataTable } from "@/components/UtilisationDataTable";

type UtilisationApiDataTableProps<T extends Record<string, unknown>> = {
  title: string;
  endpoint: string;
  filters: UtilisationFilters;
  normalizer?: ApiResponseNormalizer<T>;
  emptyMessage?: string;
  variant?: TableVariant;
  sortable?: boolean;
};

type TableState<T extends Record<string, unknown>> = {
  data: T[];
  columns: TableColumn<T>[];
  loading: boolean;
  error: string | null;
};

export function UtilisationApiDataTable<T extends Record<string, unknown>>({
  title,
  endpoint,
  filters,
  normalizer,
  emptyMessage,
  variant,
  sortable = false,
}: UtilisationApiDataTableProps<T>) {
  const [tableState, setTableState] = useState<TableState<T>>({
    data: [],
    columns: [],
    loading: true,
    error: null,
  });
  const [sort, setSort] = useState(DEFAULT_SORT);
  const handleSort = (key: string, direction: SortDir | null) => setSort(applySort(key, direction));

  useEffect(() => {
    const controller = new AbortController();

    async function load() {
      setTableState((current) => ({ ...current, loading: true, error: null }));

      try {
        const response = await fetchUtilisationReport(endpoint, filters, controller.signal, sortable ? sort : undefined);
        const normalized = normalizer ? normalizer(response) : normalizeApiResponse(response);
        if (controller.signal.aborted) return;
        setTableState({
          data: normalized.rows as T[],
          columns: normalized.columns as TableColumn<T>[],
          loading: false,
          error: null,
        });
      } catch (caught) {
        if (controller.signal.aborted) return;
        setTableState({
          data: [],
          columns: [],
          loading: false,
          error: caught instanceof Error ? caught.message : "Unable to load report data",
        });
      }
    }

    load();

    return () => controller.abort();
  }, [endpoint, filters, normalizer, sortable, sort]);

  return (
    <UtilisationDataTable
      title={title}
      data={tableState.data}
      columns={tableState.columns}
      loading={tableState.loading}
      error={tableState.error}
      emptyMessage={emptyMessage}
      variant={variant}
      sort={sortable ? sort : undefined}
      onSort={sortable ? handleSort : undefined}
    />
  );
}
